import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MarketplaceHomePackages from '../components/MarketplaceHomePackages';
import PopularDestinationsCarousel from '../components/PopularDestinationsCarousel';
import SearchBar from '../components/SearchBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import heroImage from '../data/Acceuil.jpg';
import type { PackageSearchFilters } from '../types/marketplaceSearch';
import { EMPTY_PACKAGE_SEARCH_FILTERS } from '../types/marketplaceSearch';

/** Images Unsplash représentatives (Kaaba, Istanbul, Dubaï, pyramides) — recadrage léger pour la perf. */
const destinationImage = (photoId: string) =>
  `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=1600&q=80`;

const popularDestinations = [
  {
    image: destinationImage('photo-1565019001609-0e34a6a22189'),
    name: 'Omra',
    filterDestination: 'omra',
    price: 250_000,
    primaryColor: '#2563eb',
  },
  {
    image: destinationImage('photo-1527838832700-5059252407fa'),
    name: 'Istanbul',
    filterDestination: 'istanbul',
    price: 120_000,
    primaryColor: '#2563eb',
  },
  {
    image: destinationImage('photo-1512453979798-5ea266f8880c'),
    name: 'Dubai',
    filterDestination: 'dubai',
    price: 180_000,
    primaryColor: '#2563eb',
  },
  {
    image: destinationImage('photo-1568322445389-f64ac2515020'),
    name: 'Égypte',
    filterDestination: 'égypte',
    price: 140_000,
    primaryColor: '#2563eb',
  },
  {
    image: destinationImage('photo-1506905925346-21bda4d32df4'),
    name: 'Malaisie',
    filterDestination: 'malaisie',
    price: 165_000,
    primaryColor: '#2563eb',
  },
  {
    image: destinationImage('photo-1473580044384-7ba9967e16a0'),
    name: 'Désert algérien',
    filterDestination: 'désert algérien',
    price: 135_000,
    primaryColor: '#2563eb',
  },
];

export default function HomePage() {
  const [searchFilters, setSearchFilters] = useState<PackageSearchFilters>(
    EMPTY_PACKAGE_SEARCH_FILTERS
  );

  const handleSearch = useCallback((filters: PackageSearchFilters) => {
    setSearchFilters(filters);
    requestAnimationFrame(() => {
      document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  return (
    <>
      <Navbar />

      {/* Section Hero — min-h dynamique + zone safe pour encoches */}
      <section id="search-hero" className="relative flex min-h-screen min-h-[100dvh] flex-col">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Hero"
            className="h-full w-full object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div
          className="relative mx-auto flex min-h-0 w-full max-w-7xl flex-col items-center justify-start px-4 pb-16 text-center sm:px-6 sm:pb-20 md:pb-24 lg:px-8 min-h-0 pt-[max(12.5rem,calc(env(safe-area-inset-top)+10rem))] sm:pt-[max(13.5rem,calc(env(safe-area-inset-top)+10.75rem))] md:pt-[max(15rem,calc(env(safe-area-inset-top)+11.5rem))] lg:pt-[max(16rem,calc(env(safe-area-inset-top)+12rem))]"
        >
          <h1 className="mb-5 max-w-4xl text-[clamp(1.45rem,4.8vw,3.2rem)] font-bold leading-tight text-white sm:mb-6">
            Trouvez le voyage qui vous ressemble
          </h1>
          <p className="mb-7 max-w-2xl text-[clamp(1rem,2.8vw,1.25rem)] leading-relaxed text-white/95 sm:mb-8">
            Parcourez les offres, comparez les formules et réservez votre séjour en quelques clics.
          </p>

          <SearchBar variant="hero" onSearch={handleSearch} />

          <Link
            to="/offres"
            className="mt-8 text-white/90 text-sm hover:text-white underline underline-offset-4 transition"
          >
            Voir tous les séjours
          </Link>
        </div>
      </section>

      {/* Packages — mêmes cartes que sur les sites agence (composant PackageCard) */}
      <MarketplaceHomePackages searchFilters={searchFilters} />

      {/* Section Destinations */}
      <section id="destinations" className="bg-gray-50 py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="mb-8 text-center text-2xl font-bold md:mb-10 md:text-3xl">
            Destinations populaires
          </h2>
          <PopularDestinationsCarousel items={popularDestinations} />
        </div>
      </section>

      <Footer />
    </>
  );
}
